import { motion, AnimatePresence } from 'framer-motion';
import seiIcon from '@/assets/header/sei.webp';
import type { Chest, ChestRarity } from '@types';
import CloseButton from '@/assets/svgs/CloseButton';
import { showToast } from '@/utils';

interface PurchaseChestModalProps {
  isOpen: boolean;
  chest: Chest | null;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
}

const chestRewardMap: Record<ChestRarity, string> = {
  normal: '1-3 Gold',
  platinum: '2-5 Gold',
  rare: '4-8 Gold',
  legendary: '6-12 Gold',
};

export const PurchaseChestModal = ({
  isOpen,
  chest,
  onClose,
  onConfirm,
  isLoading = false,
}: PurchaseChestModalProps) => {
  if (!chest) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={!isLoading ? onClose : undefined}
            style={{ cursor: isLoading ? 'not-allowed' : 'pointer' }}
          />

          {/* Modal Container */}
          <div className="fixed left-0 right-0 top-1/2 -translate-y-1/2 z-[101] max-w-[var(--app-max-width)] mx-auto flex justify-center items-center pointer-events-none">
            <motion.div
              className="relative w-[339px] flex flex-col items-center p-3 isolate bg-[#C2CCDF] border-2 border-[rgba(46,64,86,0.2)] rounded-2xl pointer-events-auto"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            >
              {/* Close Button */}
              <motion.div
                className="absolute top-3 right-3 cursor-pointer"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.15 }}
                onClick={onClose}
              >
                <CloseButton />
              </motion.div>

              {/* Content */}
              <div className="flex flex-col items-center gap-4 w-full max-w-[307px] pt-5">
                {/* Chest Image Container - Overlapping */}
                <motion.img
                  src={chest.imageUrl}
                  alt={chest.name}
                  className=" size-24 object-contain z-10 absolute top-5 left-7"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    type: 'spring',
                    stiffness: 200,
                  }}
                />

                {/* Chest Info Container */}
                <div className="flex flex-col items-center mt-10 pt-5 pb-4 px-4 w-full bg-[#8391A6] rounded-xl gap-4">
                  {/* Name and Reward Row */}
                  <div className="flex justify-between items-start w-full gap-4">
                    {/* Left: Chest Name */}
                    <div className="flex-1 mt-6 ml-2">
                      <h2 className="font-lilita text-[24px] text-white text-left [text-shadow:1px_2px_0px_#000] [-webkit-text-stroke:1px_#000]">
                        {chest.name}
                      </h2>
                    </div>

                    {/* Right: Reward */}
                    <div className="flex flex-col items-end">
                      <p className="font-lilita text-[18px] text-white [text-shadow:1px_1px_0px_#000] [-webkit-text-stroke:0.5px_#000]">
                        Reward
                      </p>
                      <p
                        className="font-lilita text-[22px] [text-shadow:1px_2px_0px_#000] [-webkit-text-stroke:1px_#000]"
                        style={{ color: '#FFE500' }}
                      >
                        {chestRewardMap[chest.rarity]}
                      </p>
                    </div>
                  </div>
                </div>
                {/* Price and Buy Button Row */}
                <div className="flex justify-between items-center gap-3 w-full px-2 mb-2">
                  {/* Left: Price */}
                  <div className="flex items-center gap-1">
                    <p className="font-lilita text-[22px] text-white [text-shadow:1px_2px_0px_#000] [-webkit-text-stroke:1px_#000] mb-1">
                      Price:
                    </p>
                    <img src={seiIcon} alt="SEI" className="w-7 h-7 ml-1" />
                    <span
                      className="font-lilita text-[24px] [text-shadow:1px_2px_0px_#000] [-webkit-text-stroke:1px_#000]"
                      style={{ color: '#FFE500' }}
                    >
                      {chest.price}
                    </span>
                  </div>

                  {/* Right: Buy Button */}
                  <motion.button
                    onClick={onConfirm}
                    disabled={isLoading}
                    className="relative flex items-center justify-center px-4 py-2 gap-3 isolate h-12 border-2 border-black rounded-xl overflow-hidden"
                    style={{
                      background:
                        'linear-gradient(135deg, #FF7A00 0%, #FFA500 49.04%, #FFD700 100%)',
                      opacity: isLoading ? 0.7 : 1,
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      minWidth: '140px',
                    }}
                    whileHover={!isLoading ? { scale: 1.05, y: -2 } : {}}
                    whileTap={!isLoading ? { scale: 0.95 } : {}}
                    transition={{ duration: 0.15 }}
                  >
                    <div className="absolute inset-x-2 top-2 h-7 bg-white/30 rounded-lg z-0" />
                    <span className="font-lilita text-[28px] leading-8 text-white relative z-10 [text-shadow:1px_2px_0px_#000] [-webkit-text-stroke:1px_#000]">
                      {isLoading ? 'Buying...' : 'Buy'}
                    </span>
                  </motion.button>
                </div>

                {/* Buy SEI with Card Button */}
                <div className="w-full px-2 mb-2">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex-1 h-px bg-gray-400/50" />
                    <span className="text-gray-500 text-xs font-medium">
                      Need SEI?
                    </span>
                    <div className="flex-1 h-px bg-gray-400/50" />
                  </div>
                  <motion.button
                    // onClick={() => setIsBuySeiModalOpen(true)}
                    onClick={() => showToast.custom('Coming soon')}
                    disabled={isLoading}
                    className="w-full py-2.5 bg-gradient-to-r from-indigo-500 via-purple-500 to-purple-600 rounded-xl text-white font-bold text-sm border-2 border-black"
                    style={{
                      textShadow: '1px 1px 0 #000',
                      opacity: isLoading ? 0.7 : 1,
                    }}
                    whileHover={!isLoading ? { scale: 1.02, y: -1 } : {}}
                    whileTap={!isLoading ? { scale: 0.98 } : {}}
                  >
                    <span className="flex items-center justify-center gap-2">
                      Buy SEI with Card
                    </span>
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </div>

        </>
      )}
    </AnimatePresence>
  );
};
